/**
 * Token Manager (ops-frontend)
 * Handles refresh token and access token storage and retrieval
 */

const TOKEN_KEYS = {
  REFRESH_TOKEN: 'refreshToken',
  ACCESS_TOKEN: 'accessToken',
  REDIRECT_URL: 'redirectUrl'
};

const USER_INFO_KEYS = {
  USERNAME: 'username',
  EMAIL: 'email',
  PHONE_NUMBER: 'phoneNumber',
  USER_ID: 'userId',
  COMPANY: 'company',
  TENANT_ID: 'tenantId',
  WALLET_ADDR: 'walletAddr'
};

// ─── Token Operations ────────────────────────────────────────────────────────

export const getRefreshToken = () => {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
};

export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, token);
    console.log('Refresh token saved to localStorage');
  }
};

export const getAccessToken = () => {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
};

export const setAccessToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, token);
    console.log('Access token saved to localStorage');
  }
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REDIRECT_URL);
  console.log('All tokens cleared from localStorage');
};

// ─── URL Parameter Extraction ────────────────────────────────────────────────

/**
 * Ops frontend refreshToken from URL may not include "Bearer " prefix,
 * so we automatically add it before saving.
 */
export const checkAndSaveRefreshTokenFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const refreshToken = urlParams.get('refreshToken');

  if (refreshToken) {
    let decodedToken = decodeURIComponent(refreshToken);

    if (!decodedToken.startsWith('Bearer ')) {
      decodedToken = `Bearer ${decodedToken}`;
    }

    setRefreshToken(decodedToken);
    console.log('Refresh token found in URL and saved');
    return decodedToken;
  }

  return null;
};

export const getRedirectUrl = () => {
  return localStorage.getItem(TOKEN_KEYS.REDIRECT_URL);
};

export const setRedirectUrl = (url) => {
  if (url) {
    localStorage.setItem(TOKEN_KEYS.REDIRECT_URL, url);
    console.log('Redirect URL saved to localStorage:', url);
  }
};

export const checkAndSaveRedirectUrlFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectUrl = urlParams.get('redirectUrl');

  if (redirectUrl) {
    const decodedUrl = decodeURIComponent(redirectUrl);
    setRedirectUrl(decodedUrl);
    console.log('Redirect URL found in URL and saved');
    return decodedUrl;
  }

  return null;
};

export const getCompany = () => {
  return localStorage.getItem(USER_INFO_KEYS.COMPANY);
};

export const setCompany = (company) => {
  if (company) {
    localStorage.setItem(USER_INFO_KEYS.COMPANY, company);
    console.log('Company saved to localStorage:', company);
  }
};

export const checkAndSaveCompanyFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const company = urlParams.get('company');

  if (company) {
    const decodedCompany = decodeURIComponent(company);
    setCompany(decodedCompany);
    console.log('Company found in URL and saved');
    return decodedCompany;
  }

  return null;
};

export const setUserId = (userId) => {
  if (userId) {
    localStorage.setItem(USER_INFO_KEYS.USER_ID, userId);
    console.log('User ID saved to localStorage:', userId);
  }
};

export const checkAndSaveUserIdFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const userId = urlParams.get('userId');

  if (userId) {
    const decodedUserId = decodeURIComponent(userId);
    setUserId(decodedUserId);
    console.log('User ID found in URL and saved:', decodedUserId);
    return decodedUserId;
  }

  return null;
};

export const hasValidTokens = () => {
  return !!(getRefreshToken() && getAccessToken());
};

// ─── User Info Operations ────────────────────────────────────────────────────

export const setUserInfo = (userInfo) => {
  if (!userInfo) return;

  Object.values(USER_INFO_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });

  if (userInfo.username) localStorage.setItem(USER_INFO_KEYS.USERNAME, userInfo.username);
  if (userInfo.email) localStorage.setItem(USER_INFO_KEYS.EMAIL, userInfo.email);
  if (userInfo.phoneNumber) localStorage.setItem(USER_INFO_KEYS.PHONE_NUMBER, userInfo.phoneNumber);
  if (userInfo.userId) localStorage.setItem(USER_INFO_KEYS.USER_ID, userInfo.userId);
  if (userInfo.walletAddr) localStorage.setItem(USER_INFO_KEYS.WALLET_ADDR, userInfo.walletAddr);
  if (userInfo.tenantId) {
    localStorage.setItem(USER_INFO_KEYS.TENANT_ID, userInfo.tenantId);
    localStorage.setItem(USER_INFO_KEYS.COMPANY, userInfo.tenantId);
  }

  console.log('User info saved to localStorage');
};

export const getTenantId = () => {
  return localStorage.getItem(USER_INFO_KEYS.TENANT_ID);
};

export const getUserInfo = () => {
  return {
    username: localStorage.getItem(USER_INFO_KEYS.USERNAME),
    email: localStorage.getItem(USER_INFO_KEYS.EMAIL),
    phoneNumber: localStorage.getItem(USER_INFO_KEYS.PHONE_NUMBER),
    userId: localStorage.getItem(USER_INFO_KEYS.USER_ID),
    company: localStorage.getItem(USER_INFO_KEYS.COMPANY),
    tenantId: localStorage.getItem(USER_INFO_KEYS.TENANT_ID),
    walletAddr: localStorage.getItem(USER_INFO_KEYS.WALLET_ADDR)
  };
};

export const clearUserInfo = () => {
  Object.values(USER_INFO_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
  console.log('User info cleared from localStorage');
};

export const getUsername = () => {
  return localStorage.getItem(USER_INFO_KEYS.USERNAME);
};

export const getUserId = () => {
  return localStorage.getItem(USER_INFO_KEYS.USER_ID);
};
